import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  FlaskConical, 
  Plus, 
  Trash2,
  Trophy,
  BarChart3,
  Eye,
  CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/toast";
import { 
  getABTests, 
  createABTest, 
  getABTestResults, 
  completeABTest, 
  deleteABTest 
} from "@/lib/marketing-api";

export default function ABTesting() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [results, setResults] = useState(null);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    variants: [
      { name: t('marketingFull.variantA'), message_content: "" },
      { name: t('marketingFull.variantB'), message_content: "" }
    ]
  });

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    try {
      const response = await getABTests();
      setTests(response.data?.data || response.data || []);
    } catch (error) {
      toast.error(t('errors.serverError'));
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const validVariants = formData.variants.filter(v => v.name.trim() && v.message_content.trim());
    if (validVariants.length < 2) {
      toast.error(t('marketingFull.minTwoVariants'));
      return;
    }
    try {
      await createABTest({
        ...formData,
        variants: validVariants
      });
      toast.success(t('marketingFull.abTestCreated'));
      setIsDialogOpen(false);
      setFormData({
        name: "",
        description: "",
        variants: [
          { name: t('marketingFull.variantA'), message_content: "" },
          { name: t('marketingFull.variantB'), message_content: "" }
        ]
      });
      loadTests();
    } catch (error) {
      toast.error(t('marketingFull.errorCreatingTest'));
    }
  };

  const handleViewResults = async (test) => {
    try {
      const response = await getABTestResults(test.id);
      setResults(response.data);
      setSelectedTest(test);
    } catch (error) {
      toast.error(t('marketingFull.errorLoadingResults'));
    }
  };

  const handleComplete = async (testId) => {
    try {
      await completeABTest(testId);
      toast.success(t('marketingFull.testCompleted'));
      loadTests();
      if (selectedTest?.id === testId) {
        handleViewResults({ id: testId });
      }
    } catch (error) {
      toast.error(t('marketingFull.errorCompletingTest'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('marketingFull.confirmDeleteTest'))) return;
    try {
      await deleteABTest(id);
      toast.success(t('marketingFull.testDeleted'));
      loadTests();
      if (selectedTest?.id === id) {
        setSelectedTest(null);
        setResults(null);
      }
    } catch (error) {
      toast.error(t('marketingFull.errorDeletingTest'));
    }
  };

  const addVariant = () => {
    const variantLetter = String.fromCharCode(65 + formData.variants.length);
    setFormData({
      ...formData,
      variants: [...formData.variants, { name: `${t('marketingFull.variantA').split(' ')[0]} ${variantLetter}`, message_content: "" }]
    });
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setFormData({ ...formData, variants: newVariants });
  };

  const removeVariant = (index) => {
    if (formData.variants.length <= 2) return;
    const newVariants = formData.variants.filter((_, i) => i !== index);
    setFormData({ ...formData, variants: newVariants });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ab-testing-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('marketingFull.abTestingTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('marketingFull.abTestingSubtitle')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-2 shadow-lg shadow-emerald-500/30 text-base" size="lg" data-testid="create-ab-test-btn">
              <Plus className="w-5 h-5 me-2" />
              + {t('marketingFull.newTest')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" dir={document.documentElement.dir}>
            <DialogHeader>
              <DialogTitle>{t('marketingFull.createTest')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label htmlFor="name">{t('marketingFull.testName')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('marketingFull.testName')}
                  required
                  data-testid="ab-test-name-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="description">{t('marketingFull.testDescription')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('marketingFull.testDescPlaceholder')}
                  data-testid="ab-test-description-input"
                />
              </div>
              <div className="form-group">
                <div className="flex items-center justify-between mb-2">
                  <Label>{t('marketingFull.messageVariants')}</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addVariant}
                    data-testid="add-variant-btn"
                  >
                    <Plus className="w-4 h-4 me-1" />
                    {t('marketingFull.addVariant')}
                  </Button>
                </div>
                <div className="space-y-4">
                  {formData.variants.map((variant, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Input
                          value={variant.name}
                          onChange={(e) => updateVariant(index, 'name', e.target.value)}
                          placeholder={`${t('marketingFull.variantA').split(' ')[0]} ${String.fromCharCode(65 + index)}`}
                          className="w-40"
                          data-testid={`variant-name-${index}`}
                        />
                        {formData.variants.length > 2 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeVariant(index)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={variant.message_content}
                        onChange={(e) => updateVariant(index, 'message_content', e.target.value)}
                        placeholder={t('marketingFull.messageContent')}
                        rows={3}
                        data-testid={`variant-content-${index}`}
                      />
                    </Card>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" data-testid="submit-ab-test-btn">
                  {t('marketingFull.createTest')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tests List */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold">{t('marketingFull.tests')}</h2>
          {tests.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="empty-state">
                  <FlaskConical className="empty-state-icon" />
                  <h3 className="empty-state-title">{t('marketingFull.noTests')}</h3>
                  <p className="empty-state-description">{t('marketingFull.createFirstTest')}</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            tests.map((test) => (
              <Card 
                key={test.id} 
                className={`card-hover cursor-pointer ${selectedTest?.id === test.id ? 'ring-2 ring-primary' : ''}`}
                onClick={() => handleViewResults(test)}
                data-testid={`ab-test-card-${test.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold">{test.name}</h3>
                        {test.is_active ? (
                          <Badge variant="outline" className="text-green-400 border-green-400">
                            {t('marketingFull.active')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            {t('marketingFull.completed')}
                          </Badge>
                        )}
                      </div>
                      {test.description && (
                        <p className="text-sm text-muted-foreground mt-1">{test.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        {test.variants?.length || 0} {t('marketingFull.messageVariants')}
                      </p>
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleViewResults(test)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {test.is_active && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleComplete(test.id)}
                          data-testid={`complete-test-${test.id}`}
                        >
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-destructive"
                        onClick={() => handleDelete(test.id)}
                        data-testid={`delete-test-${test.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Results Panel */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold">{t('marketingFull.results')}</h2>
          {results ? (
            <Card data-testid="ab-test-results">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  {results.test_name}
                </CardTitle>
                <CardDescription>
                  {t('marketingFull.totalMessagesSent')} {results.total_sent}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {results.variants.map((variant, index) => {
                  const isWinner = results.winner?.variant_id === variant.variant_id;
                  return (
                    <div 
                      key={variant.variant_id} 
                      className={`p-4 rounded-lg border ${isWinner ? 'border-green-500 bg-green-500/10' : 'border-border'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-bold">{variant.variant_name}</span>
                          {isWinner && (
                            <Badge className="bg-green-500">
                              <Trophy className="w-3 h-3 me-1" />
                              {t('marketingFull.winner')}
                            </Badge>
                          )}
                        </div>
                        <span className="font-numbers text-sm">
                          {variant.sent_count} {t('marketingFull.variantSent')}
                        </span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{t('marketingFull.deliveryRate')}</span>
                            <span className="font-numbers">{variant.delivery_rate}%</span>
                          </div>
                          <Progress value={variant.delivery_rate} />
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{t('marketingFull.openRate')}</span>
                            <span className="font-numbers">{variant.open_rate}%</span>
                          </div>
                          <Progress value={variant.open_rate} className="bg-muted" />
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{t('marketingFull.clickRate')}</span>
                            <span className="font-numbers">{variant.click_rate}%</span>
                          </div>
                          <Progress value={variant.click_rate} className="bg-muted" />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {results.is_completed && results.winner && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-green-400" />
                      <span className="font-bold text-green-400">{t('marketingFull.winner')}: {results.winner.variant_name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t('marketingFull.winnerAchieved')} {results.winner.sent_count}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="empty-state">
                  <BarChart3 className="empty-state-icon" />
                  <h3 className="empty-state-title">{t('marketingFull.selectTestToViewResults')}</h3>
                  <p className="empty-state-description">{t('marketingFull.selectTestToViewResults')}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Tips */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <h4 className="font-medium mb-2">💡 {t('marketingFull.abTestTips')}</h4>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• {t('marketingFull.abTestTip1')}</li>
            <li>• {t('marketingFull.abTestTip2')}</li>
            <li>• {t('marketingFull.abTestTip3')}</li>
            <li>• {t('marketingFull.abTestTip4')}</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
